import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_66258ad9-e000-456c-bbb1-9b3da697cdba/artifacts/pl46n86d_New%20SCAH%20Logo.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Legislative Victories", path: "/legislative-victories" },
    { name: "Events", path: "/events" },
    { name: "News", path: "/news" },
    { name: "Resources", path: "/resources" },
    { name: "Members", path: "/members" },
    { name: "Gallery", path: "/gallery" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-slate-100 py-2"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="navbar-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="Sickle Cell Association" 
              className="h-14 w-auto"
            />
            <div className="hidden sm:block">
              <p className="font-serif font-semibold text-slate-900 text-lg leading-tight">Sickle Cell</p>
              <p className="text-xs text-red-600 tracking-wider uppercase font-medium">Association</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? "text-teal-700"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Donate Button & Mobile Menu Toggle */}
          <div className="flex items-center gap-4">
            <Link to="/donate">
              <Button 
                className="bg-amber-700 hover:bg-amber-800 text-white font-sans font-bold tracking-wide uppercase text-xs px-6 py-2 btn-accent"
                data-testid="donate-btn"
              >
                <Heart className="w-4 h-4 mr-2" />
                Donate
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? (
                <X className="w-6 h-6 text-slate-700" />
              ) : (
                <Menu className="w-6 h-6 text-slate-700" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="lg:hidden mt-4 pb-4 border-t border-slate-100 pt-4"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? "bg-teal-50 text-teal-700"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
